import type { JobKind, JobMap, JobRequest, JobResponse } from '../worker/protocol';

interface PendingJob<K extends JobKind = JobKind> {
  id: number;
  kind: K;
  seed: number;
  params: JobMap[K]['params'];
  priority: number;
  key: string | undefined;
  resolve: (r: JobMap[K]['result']) => void;
  reject: (e: Error) => void;
}

export class CancelledError extends Error {
  constructor() {
    super('cancelled');
  }
}

/**
 * Fixed-size pool of module workers with a priority queue (lower number runs first).
 * Jobs with a key can be cancelled while still queued; running jobs finish but their result is dropped.
 */
export class WorkerPool {
  private readonly workers: Worker[] = [];
  private readonly busy: (PendingJob | null)[] = [];
  private queue: PendingJob[] = [];
  private nextId = 1;
  private readonly cancelledKeys = new Set<string>();
  completed = 0;
  lastJobMs = 0;

  constructor(size: number) {
    for (let i = 0; i < size; i++) {
      const w = new Worker(new URL('../worker/genWorker.ts', import.meta.url), { type: 'module' });
      w.onmessage = (ev: MessageEvent<JobResponse>) => this.onMessage(i, ev.data);
      w.onerror = (ev) => {
        console.error('worker error', ev.message);
        const job = this.busy[i];
        this.busy[i] = null;
        job?.reject(new Error(ev.message));
        this.pump();
      };
      this.workers.push(w);
      this.busy.push(null);
    }
  }

  get size(): number {
    return this.workers.length;
  }

  get queueDepth(): number {
    return this.queue.length;
  }

  get inFlight(): number {
    return this.busy.filter((b) => b !== null).length;
  }

  submit<K extends JobKind>(kind: K, seed: number, params: JobMap[K]['params'], priority = 0, key?: string): Promise<JobMap[K]['result']> {
    if (key) this.cancelledKeys.delete(key);
    return new Promise((resolve, reject) => {
      const job: PendingJob<K> = { id: this.nextId++, kind, seed, params, priority, key, resolve, reject };
      this.queue.push(job as unknown as PendingJob);
      this.pump();
    });
  }

  /** Change priority of queued jobs, e.g. when the camera moves. */
  reprioritize(fn: (key: string | undefined, old: number) => number): void {
    for (const j of this.queue) j.priority = fn(j.key, j.priority);
  }

  cancel(key: string): void {
    this.cancelledKeys.add(key);
    const keep: PendingJob[] = [];
    for (const j of this.queue) {
      if (j.key === key) j.reject(new CancelledError());
      else keep.push(j);
    }
    this.queue = keep;
  }

  cancelWhere(pred: (key: string) => boolean): void {
    const keep: PendingJob[] = [];
    for (const j of this.queue) {
      if (j.key !== undefined && pred(j.key)) {
        this.cancelledKeys.add(j.key);
        j.reject(new CancelledError());
      } else keep.push(j);
    }
    this.queue = keep;
    for (const b of this.busy) if (b?.key !== undefined && pred(b.key)) this.cancelledKeys.add(b.key);
  }

  private pump(): void {
    for (let i = 0; i < this.workers.length; i++) {
      if (this.busy[i] || this.queue.length === 0) continue;
      let best = 0;
      for (let q = 1; q < this.queue.length; q++) if (this.queue[q].priority < this.queue[best].priority) best = q;
      const job = this.queue.splice(best, 1)[0];
      this.busy[i] = job;
      const req: JobRequest = { id: job.id, kind: job.kind, seed: job.seed, params: job.params };
      this.workers[i].postMessage(req);
    }
  }

  private onMessage(i: number, res: JobResponse): void {
    const job = this.busy[i];
    this.busy[i] = null;
    this.completed++;
    this.lastJobMs = res.ms;
    if (job && job.id === res.id) {
      if (job.key !== undefined && this.cancelledKeys.has(job.key)) {
        this.cancelledKeys.delete(job.key);
        job.reject(new CancelledError());
      } else if (res.ok) job.resolve(res.result as never);
      else job.reject(new Error(res.error));
    }
    this.pump();
  }

  terminate(): void {
    for (const w of this.workers) w.terminate();
    for (const j of this.queue) j.reject(new CancelledError());
    this.queue = [];
  }
}
